import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Film, Plus, Minus } from "lucide-react";

const NewCampaign = () => {
    const [access, setAccess] = useState(null);
    const [pricing, setPricing] = useState(null);
    const [form, setForm] = useState({ title: "", brief: "", requirements: "", videos_count: 1 });
    const [addons, setAddons] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/brand/access").then((r) => setAccess(r.data));
        api.get("/public/pricing").then((r) => setPricing(r.data));
    }, []);

    const total = access?.videos?.total ?? 0;
    const addonTotal = (pricing?.addons || []).reduce(
        (sum, a) => sum + (addons[a.id] || 0) * a.price, 0);

    const setAddonQty = (id, delta) =>
        setAddons((prev) => ({ ...prev, [id]: Math.max(0, (prev[id] || 0) + delta) }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true); setError("");
        try {
            const r = await api.post("/brand/campaigns", form);
            const campaignId = r.data.campaign_id;
            // Pay for the first selected add-on format (others can be added later)
            const selected = Object.entries(addons).filter(([, q]) => q > 0);
            if (selected.length > 0) {
                const [formatId, quantity] = selected[0];
                const pay = await api.post("/payments/addon/checkout", {
                    origin_url: window.location.origin,
                    campaign_id: campaignId, format_id: formatId, quantity,
                });
                if (pay.data.demo) { toast.success("Add-on applied (demo)"); navigate("/brand/campaigns"); return; }
                window.location.href = pay.data.url;
                return;
            }
            toast.success("Campaign submitted — it's in the matching queue");
            navigate("/brand/campaigns");
        } catch (err) {
            setError(err.response?.data?.detail || "Could not create campaign");
            setSubmitting(false);
        }
    };

    return (
        <DashboardShell role="brand" title="New campaign" subtitle="Brief a project — videos come from your allowance">
            <form onSubmit={submit} data-testid="campaign-form" className="max-w-3xl mx-auto w-full yc-card p-8 space-y-5">
                {/* Allowance strip */}
                <div className={`border-2 border-yc-ink rounded-2xl p-4 flex items-center justify-between ${total > 0 ? "bg-yc-cream" : "bg-yc-pink text-white"}`} data-testid="allowance-strip">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                        <Film className="w-4 h-4" />
                        {total > 0
                            ? <span data-testid="allowance-count">{total} video{total === 1 ? "" : "s"} available in your allowance</span>
                            : <span>No videos in your allowance yet</span>}
                    </div>
                    {total === 0 && !access?.is_demo && (
                        <Link to="/brand/membership" data-testid="allowance-topup-cta" className="yc-btn-primary bg-white text-yc-ink text-xs py-1.5 px-3 shrink-0">Get a plan or top up →</Link>
                    )}
                </div>
                <div>
                    <label className="yc-overline">Campaign title</label>
                    <input data-testid="campaign-title" required className="yc-input mt-2" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div>
                    <label className="yc-overline">Brief</label>
                    <textarea data-testid="campaign-brief" required rows={5} className="yc-input mt-2" placeholder="Goals, style, audience…" value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} />
                </div>
                <div>
                    <label className="yc-overline">Requirements / deliverables</label>
                    <textarea data-testid="campaign-requirements" rows={3} className="yc-input mt-2" placeholder="Aspect ratios, durations, mandatory elements…" value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} />
                </div>
                <div>
                    <label className="yc-overline">Standard videos (from your allowance)</label>
                    <div className="flex items-center gap-3 mt-2">
                        <button type="button" data-testid="videos-minus" onClick={() => setForm({ ...form, videos_count: Math.max(1, form.videos_count - 1) })} className="w-10 h-10 rounded-full border-2 border-yc-ink bg-white flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                        <div className="font-heading font-black text-4xl w-14 text-center" data-testid="videos-count">{form.videos_count}</div>
                        <button type="button" data-testid="videos-plus" onClick={() => setForm({ ...form, videos_count: form.videos_count + 1 })} className="w-10 h-10 rounded-full border-2 border-yc-ink bg-white flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                        {form.videos_count > total && !access?.is_demo && (
                            <span className="text-xs text-yc-pink font-semibold" data-testid="videos-over-allowance">Over your allowance — top up first</span>
                        )}
                    </div>
                </div>
                <div>
                    <label className="yc-overline mb-3 block">Add-on formats (optional, paid per piece)</label>
                    <div className="space-y-3">
                        {(pricing?.addons || []).map((a) => (
                            <div key={a.id} className="flex items-center justify-between border-2 border-yc-ink rounded-2xl p-4 bg-white" data-testid={`addon-row-${a.id}`}>
                                <div>
                                    <div className="font-semibold text-sm">{a.name} · £{a.price.toFixed(0)}</div>
                                    <div className="text-xs text-yc-ink/60">{a.desc}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button type="button" data-testid={`addon-minus-${a.id}`} onClick={() => setAddonQty(a.id, -1)} className="w-8 h-8 rounded-full border-2 border-yc-ink flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                                    <span className="font-heading font-bold w-6 text-center" data-testid={`addon-qty-${a.id}`}>{addons[a.id] || 0}</span>
                                    <button type="button" data-testid={`addon-plus-${a.id}`} onClick={() => setAddonQty(a.id, 1)} className="w-8 h-8 rounded-full border-2 border-yc-ink flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                    {addonTotal > 0 && <div className="text-sm font-semibold mt-3" data-testid="addon-total">Add-ons total: £{addonTotal.toLocaleString()} (paid at checkout)</div>}
                </div>
                {error && <div className="text-yc-pink text-sm" data-testid="campaign-error">{error}</div>}
                <button data-testid="campaign-submit" disabled={submitting} className="yc-btn-primary">
                    {submitting ? "Submitting…" : addonTotal > 0 ? "Submit & pay add-ons" : "Submit campaign"}
                </button>
                <p className="text-xs text-yc-ink/60">Your campaign enters the matching queue. A strategist will hand-pick a creator from a ranked shortlist. Standard videos are covered by your plan allowance.</p>
            </form>
        </DashboardShell>
    );
};

export default NewCampaign;
